import {
  type BillingProvider,
  resolveBillingProvider,
} from './billing';
import {
  INTEGRATIONS_COMING_SOON_FEATURE,
  INTEGRATIONS_LIVE_PRO_FEATURE,
  integrationsLiveEnabled,
} from './product-flags';

export type { BillingProvider, BillingProviderCopy } from './billing';
export { resolveBillingProvider, getBillingProviderCopy, resolveBillingProviderCopy } from './billing';

export const PLAN_TIERS = ['lite', 'starter', 'growth', 'pro'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

export interface PlanLimit {
  activeAgreements: number;
  customers: number;
  emailsPerMonth: number;
  customTemplates: number;
  gracePercent: number;
  graceDays: number;
}

export interface PlanDef {
  id: PlanTier;
  name: string;
  tagline: string;
  /** Default monthly price in whole dollars. Overridable per-deployment via env. */
  priceMonthly: number;
  /** Env var holding the Paddle price id for this plan (pri_…). */
  paddlePriceEnvKey: string;
  /** Env var holding the Dodo product id for this plan (prod_… / pdt_…). */
  dodoProductEnvKey: string;
  /** Env var holding the Stripe price id for this plan (legacy). */
  stripePriceEnvKey: string;
  /** Env var that can override the displayed monthly price. */
  priceAmountEnvKey: string;
  recommended?: boolean;
  limits: PlanLimit;
  features: string[];
}

const GRACE = { gracePercent: 0.1, graceDays: 7 };

/**
 * The plan ladder. "Lite" is the negligible, no-brainer entry plan so any shop
 * can start; "Growth" is the value tier most retention-focused shops land on.
 * Prices and Stripe price ids are overridable via environment variables so they
 * can be tuned per deployment without a code change.
 */
export const PLAN_CATALOG: PlanDef[] = [
  {
    id: 'lite',
    name: 'Lite',
    tagline: 'Get off spreadsheets today.',
    priceMonthly: 19,
    paddlePriceEnvKey: 'PADDLE_PRICE_LITE',
    dodoProductEnvKey: 'DODO_PRODUCT_LITE',
    stripePriceEnvKey: 'STRIPE_LITE_PRICE_ID',
    priceAmountEnvKey: 'PLAN_PRICE_LITE',
    limits: { activeAgreements: 25, customers: 50, emailsPerMonth: 250, customTemplates: 0, ...GRACE },
    features: [
      'Up to 25 active agreements',
      'Renewal risk dashboard',
      'Automated renewal campaigns',
      'Revenue-at-risk overview',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For solo shops getting organized.',
    priceMonthly: 49,
    paddlePriceEnvKey: 'PADDLE_PRICE_STARTER',
    dodoProductEnvKey: 'DODO_PRODUCT_STARTER',
    stripePriceEnvKey: 'STRIPE_STARTER_PRICE_ID',
    priceAmountEnvKey: 'PLAN_PRICE_STARTER',
    limits: { activeAgreements: 150, customers: 300, emailsPerMonth: 1000, customTemplates: 1, ...GRACE },
    features: [
      'Up to 150 active agreements',
      'Everything in Lite',
      'Optional link to your renewal page in emails',
      'Custom email template',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'Renewal automation for growing shops.',
    priceMonthly: 129,
    paddlePriceEnvKey: 'PADDLE_PRICE_GROWTH',
    dodoProductEnvKey: 'DODO_PRODUCT_GROWTH',
    stripePriceEnvKey: 'STRIPE_GROWTH_PRICE_ID',
    priceAmountEnvKey: 'PLAN_PRICE_GROWTH',
    recommended: true,
    limits: { activeAgreements: 600, customers: 1500, emailsPerMonth: 5000, customTemplates: 3, ...GRACE },
    features: [
      'Up to 600 active agreements',
      'Everything in Starter',
      'At-risk alerts + "call this week" list',
      'Win-back campaigns',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For multi-location and high-volume books.',
    priceMonthly: 249,
    paddlePriceEnvKey: 'PADDLE_PRICE_PRO',
    dodoProductEnvKey: 'DODO_PRODUCT_PRO',
    stripePriceEnvKey: 'STRIPE_PRO_PRICE_ID',
    priceAmountEnvKey: 'PLAN_PRICE_PRO',
    limits: { activeAgreements: 2500, customers: 8000, emailsPerMonth: 25000, customTemplates: 10, ...GRACE },
    features: [
      'Up to 2,500 active agreements',
      'Everything in Growth',
      INTEGRATIONS_LIVE_PRO_FEATURE,
      'Membership conversion + technician insights',
      'Multi-location + priority support',
    ],
  },
];

export const PLAN_LIMITS: Record<PlanTier, PlanLimit> = PLAN_CATALOG.reduce(
  (acc, p) => {
    acc[p.id] = p.limits;
    return acc;
  },
  {} as Record<PlanTier, PlanLimit>,
);

/** The plan used during trial and as the fallback for unconfigured workspaces. */
export const DEFAULT_PLAN_TIER: PlanTier = 'lite';
/** Trial grants the top tier's capabilities. */
export const TRIAL_PLAN_TIER: PlanTier = 'pro';

export const DEFAULT_REMINDER_OFFSETS = [-60, -30, -14, -7, -1] as const;

/** Default email-only renewal campaign steps (offsets relative to end date). */
export interface CampaignStepDef {
  offsetDays: number;
  templateKey: string;
  recipient: 'contact' | 'owner';
  offerType?: 'none' | 'percent' | 'fixed';
  offerValue?: number;
  offerCode?: string;
}

export const DEFAULT_WINBACK_CAMPAIGN_STEPS: CampaignStepDef[] = [
  { offsetDays: 14, templateKey: 'winback_14d', recipient: 'contact' },
  { offsetDays: 30, templateKey: 'winback_30d', recipient: 'contact' },
  {
    offsetDays: 60,
    templateKey: 'winback_60d',
    recipient: 'contact',
    offerType: 'percent',
    offerValue: 15,
    offerCode: 'WINBACK15',
  },
  { offsetDays: 90, templateKey: 'winback_90d', recipient: 'contact' },
  { offsetDays: 180, templateKey: 'winback_180d', recipient: 'contact' },
];

/** Days after end date before an agreement moves to lapsed and win-back starts. */
export const DEFAULT_LAPSE_GRACE_DAYS = GRACE.graceDays;

export const DEFAULT_RENEWAL_CAMPAIGN_STEPS: CampaignStepDef[] = [
  { offsetDays: -60, templateKey: 'renewal_60d', recipient: 'contact' },
  { offsetDays: -30, templateKey: 'renewal_30d', recipient: 'contact' },
  { offsetDays: -14, templateKey: 'renewal_14d', recipient: 'contact' },
  { offsetDays: -7, templateKey: 'renewal_7d', recipient: 'contact' },
  { offsetDays: -7, templateKey: 'owner_renewal_7d', recipient: 'owner' },
  { offsetDays: -1, templateKey: 'renewal_1d', recipient: 'contact' },
  { offsetDays: 3, templateKey: 'renewal_lapsed', recipient: 'contact' },
  { offsetDays: 14, templateKey: 'renewal_winback_14d', recipient: 'contact' },
  { offsetDays: 7, templateKey: 'owner_lapsed_digest', recipient: 'owner' },
];

export const OFFSET_TEMPLATE_MAP: Record<number, { customer?: string; owner?: string }> = {
  [-60]: { customer: 'renewal_60d' },
  [-30]: { customer: 'renewal_30d' },
  [-14]: { customer: 'renewal_14d' },
  [-7]: { customer: 'renewal_7d', owner: 'owner_renewal_7d' },
  [-1]: { customer: 'renewal_1d' },
  [3]: { customer: 'renewal_lapsed' },
  [14]: { customer: 'renewal_winback_14d' },
  [7]: { owner: 'owner_lapsed_digest' },
};

export const POST_EXPIRY_OFFSETS = [3, 7, 14] as const;

export const SEND_WINDOW = { startHour: 9, endHour: 18 } as const;

/** Default trial length in days. Overridable per-deployment via the TRIAL_DAYS env var. */
export const DEFAULT_TRIAL_DAYS = 7;

export const SESSION_COOKIE = 'mt_session';
export const SESSION_TTL_SECONDS = 30 * 24 * 60 * 60;

export const OBLIGATION_STATUSES = [
  'draft',
  'active',
  'paused',
  'lapsed',
  'renewed',
  'paid',
  'declined',
  'archived',
] as const;

export type ObligationStatus = (typeof OBLIGATION_STATUSES)[number];

export const ACTIVE_OBLIGATION_STATUSES: ObligationStatus[] = ['draft', 'active', 'paused'];

export const TERMINAL_OBLIGATION_STATUSES: ObligationStatus[] = ['renewed', 'paid', 'declined', 'archived'];

export const TRADE_TYPES = ['hvac', 'plumbing', 'electrical', 'other'] as const;
export const INTERVAL_PRESETS = ['annual', 'semi_annual', 'quarterly', 'custom'] as const;

export type TradeType = (typeof TRADE_TYPES)[number];
export type IntervalPreset = (typeof INTERVAL_PRESETS)[number];

/** Human-readable labels for trade type selects (HVAC stays all-caps). */
export const TRADE_TYPE_LABELS: Record<TradeType, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  other: 'Other',
};

/** Human-readable labels for billing interval presets. */
export const INTERVAL_PRESET_LABELS: Record<IntervalPreset, string> = {
  annual: 'Annual',
  semi_annual: 'Semi-annual',
  quarterly: 'Quarterly',
  custom: 'Custom',
};

export function formatTradeType(value: string): string {
  return TRADE_TYPE_LABELS[value as TradeType] ?? value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatIntervalPreset(value: string): string {
  return INTERVAL_PRESET_LABELS[value as IntervalPreset] ?? value.replace(/_/g, ' ');
}

/** Default first-agreement plan name shown during onboarding per trade. */
export const DEFAULT_PLAN_NAME_BY_TRADE: Record<TradeType, string> = {
  hvac: 'Annual HVAC Maintenance',
  plumbing: 'Annual Plumbing Maintenance',
  electrical: 'Annual Electrical Maintenance',
  other: 'Annual Maintenance Plan',
};

export function getDefaultPlanNameForTrade(trade: TradeType): string {
  return DEFAULT_PLAN_NAME_BY_TRADE[trade];
}

export function isDefaultPlanNameForTrade(name: string): boolean {
  return (Object.values(DEFAULT_PLAN_NAME_BY_TRADE) as string[]).includes(name);
}
export const CURRENCIES = ['USD', 'EUR'] as const;
export const CONTACT_TYPES = ['customer', 'subcontractor', 'vendor', 'lead'] as const;
export const PAUSE_REASONS = ['dispute', 'customer_request', 'seasonal', 'other'] as const;
export const PAYMENT_METHODS = ['stripe', 'check', 'cash', 'other'] as const;
export const DIGEST_FREQUENCIES = ['off', 'daily', 'weekly'] as const;

export interface RenewalMetadata {
  plan_name: string;
  interval_months: number;
  trade_equipment?: string | null;
  service_inclusions?: string | null;
  auto_renew?: boolean;
  payment_link_enabled?: boolean;
  last_service_date?: string | null;
  renewal_count?: number;
  pause_reason?: (typeof PAUSE_REASONS)[number] | null;
  service_visit?: boolean;
  notes?: string;
}

/**
 * Resolve the active plan tier from a Stripe price id using a price-id → tier
 * map (built from env). Falls back to the entry tier when unconfigured.
 */
export function getPlanTier(
  planId: string | null | undefined,
  priceMap: Record<string, PlanTier>,
): PlanTier {
  // Dev/e2e and manual overrides may store the tier slug directly on workspace.planId.
  if (planId && (PLAN_TIERS as readonly string[]).includes(planId)) {
    return planId as PlanTier;
  }
  if (planId && priceMap[planId]) return priceMap[planId];
  return DEFAULT_PLAN_TIER;
}

/** One-click renewal payment links — Starter tier and above. */
export function planAllowsPaymentLinks(tier: PlanTier): boolean {
  return tier !== 'lite';
}

/** Off-session auto-renew on stored card — Growth and Pro. */
export function planAllowsAutoRenew(tier: PlanTier): boolean {
  return tier === 'growth' || tier === 'pro';
}

/** Full churn cohort forecast + trend — Growth and Pro. Basic heuristic on all tiers. */
export function planAllowsFullChurnForecast(tier: PlanTier): boolean {
  return tier === 'growth' || tier === 'pro';
}

/** Win-back campaigns — Growth and Pro. */
export function planAllowsWinback(tier: PlanTier): boolean {
  return tier === 'growth' || tier === 'pro';
}

/** Membership conversion funnel — Pro. */
export function planAllowsMembershipConversion(tier: PlanTier): boolean {
  return tier === 'pro';
}

export const DEFAULT_MEMBERSHIP_CAMPAIGN_STEPS: CampaignStepDef[] = [
  { offsetDays: 0, templateKey: 'membership_pitch', recipient: 'contact' },
  { offsetDays: 3, templateKey: 'membership_followup', recipient: 'contact' },
  { offsetDays: 7, templateKey: 'membership_final', recipient: 'contact' },
];

export const OPPORTUNITY_STAGES = ['identified', 'offered', 'converted', 'lost'] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

export const EQUIPMENT_TYPES = ['furnace', 'ac', 'water_heater', 'boiler', 'other'] as const;
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number];

/** Midpoint of typical expected life by equipment type (years). */
export const DEFAULT_EQUIPMENT_LIFE_YEARS: Record<EquipmentType, number> = {
  furnace: 18,
  ac: 12,
  water_heater: 10,
  boiler: 18,
  other: 15,
};

/** Default replacement job value by equipment type (USD). */
export const DEFAULT_UPGRADE_JOB_VALUES: Record<EquipmentType, number> = {
  furnace: 5500,
  ac: 7500,
  water_heater: 1800,
  boiler: 6000,
  other: 3500,
};

export const UPGRADE_AGE_THRESHOLD_YEARS = 10;
/** Flag when equipment is at or past this % of expected life. */
export const UPGRADE_LIFE_THRESHOLD_PERCENT = 85;

export function equipmentLifePercent(installYear: number, expectedLifeYears: number, now = new Date()): number {
  const age = Math.max(0, now.getFullYear() - installYear);
  return Math.round((age / Math.max(expectedLifeYears, 1)) * 100);
}

export function isUpgradeCandidate(
  installYear: number,
  expectedLifeYears: number,
  now = new Date(),
): boolean {
  const age = now.getFullYear() - installYear;
  return age >= UPGRADE_AGE_THRESHOLD_YEARS || equipmentLifePercent(installYear, expectedLifeYears, now) >= UPGRADE_LIFE_THRESHOLD_PERCENT;
}

/** Upgrade list + replacement quote campaign — Growth and Pro. */
export function planAllowsUpgradeCampaign(tier: PlanTier): boolean {
  return tier === 'growth' || tier === 'pro';
}

export const DEFAULT_UPGRADE_CAMPAIGN_STEPS: CampaignStepDef[] = [
  { offsetDays: 0, templateKey: 'upgrade_quote', recipient: 'contact' },
  { offsetDays: 3, templateKey: 'upgrade_followup', recipient: 'contact' },
  { offsetDays: 7, templateKey: 'upgrade_final', recipient: 'contact' },
];

/** Minimum agreement outcomes before ranking a technician. */
export const MIN_TECHNICIAN_SAMPLE_SIZE = 10;

/** Technician retention coaching insights — Pro only. */
export function planAllowsTechnicianInsights(tier: PlanTier): boolean {
  return tier === 'pro';
}

export const INTEGRATION_PROVIDERS = ['quickbooks', 'housecallpro', 'jobber', 'servicetitan'] as const;
export type IntegrationProviderKey = (typeof INTEGRATION_PROVIDERS)[number];

/** Live FSM/accounting integrations — Pro only. CSV import remains available on all tiers. */
export function planAllowsIntegrations(tier: PlanTier): boolean {
  return tier === 'pro';
}

/** Parse API period strings like `12m` into month count. */
export function parseTechnicianPeriod(period: string | undefined): number {
  const match = (period ?? '12m').match(/^(\d+)m$/i);
  if (!match) return 12;
  return Math.min(36, Math.max(1, parseInt(match[1], 10) || 12));
}

/** Lightweight env accessor — any object/function that returns string values by key. */
export type EnvGetter = (key: string) => string | undefined;

function makeGetter(env: Record<string, unknown> | EnvGetter): EnvGetter {
  return typeof env === 'function'
    ? env
    : (key: string) => {
        const v = env[key];
        return typeof v === 'string' && v.length ? v : undefined;
      };
}

function priceEnvKeyForProvider(p: PlanDef, provider: BillingProvider): string {
  if (provider === 'paddle') return p.paddlePriceEnvKey;
  if (provider === 'dodo') return p.dodoProductEnvKey;
  return p.stripePriceEnvKey;
}

/** Build a provider price-id → plan tier map from configured env price ids. */
export function resolvePriceMap(env: Record<string, unknown> | EnvGetter): Record<string, PlanTier> {
  const get = makeGetter(env);
  const provider = resolveBillingProvider(env);
  const map: Record<string, PlanTier> = {};
  for (const p of PLAN_CATALOG) {
    const id = get(priceEnvKeyForProvider(p, provider));
    if (id) map[id] = p.id;
  }
  return map;
}

export interface ResolvedPlan {
  id: PlanTier;
  name: string;
  tagline: string;
  priceMonthly: number;
  recommended: boolean;
  limits: PlanLimit;
  features: string[];
  /** Paddle or Stripe price id if configured for this deployment, else null. */
  priceId: string | null;
  billingProvider: BillingProvider;
}

/** Resolve the public plan catalog with env price overrides applied. */
function resolvePlanFeatures(plan: PlanDef, env: Record<string, unknown> | EnvGetter): string[] {
  if (plan.id !== 'pro' || integrationsLiveEnabled(env)) return plan.features;
  return plan.features.map((f) =>
    f === INTEGRATIONS_LIVE_PRO_FEATURE ? INTEGRATIONS_COMING_SOON_FEATURE : f,
  );
}

export function resolvePlanCatalog(env: Record<string, unknown> | EnvGetter): ResolvedPlan[] {
  const get = makeGetter(env);
  const provider = resolveBillingProvider(env);
  return PLAN_CATALOG.map((p) => {
    const override = Number(get(p.priceAmountEnvKey));
    return {
      id: p.id,
      name: p.name,
      tagline: p.tagline,
      priceMonthly: Number.isFinite(override) && override > 0 ? override : p.priceMonthly,
      recommended: p.recommended ?? false,
      limits: p.limits,
      features: resolvePlanFeatures(p, env),
      priceId: get(priceEnvKeyForProvider(p, provider)) ?? null,
      billingProvider: provider,
    };
  });
}

/** Trial length in days, from the TRIAL_DAYS env var with a safe default. */
export function resolveTrialDays(env: Record<string, unknown> | EnvGetter): number {
  const get = makeGetter(env);
  const v = Number(get('TRIAL_DAYS'));
  return Number.isFinite(v) && v > 0 ? Math.floor(v) : DEFAULT_TRIAL_DAYS;
}

export function intervalMonthsFromPreset(preset: string): number {
  switch (preset) {
    case 'annual':
      return 12;
    case 'semi_annual':
      return 6;
    case 'quarterly':
      return 3;
    default:
      return 12;
  }
}

export function formatRelativeDate(endDate: string): string {
  const end = new Date(endDate + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diffDays = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return `${overdue} day${overdue === 1 ? '' : 's'} overdue`;
  }
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays} days`;
}

export function getAgreementDisplayStatus(
  status: ObligationStatus,
  endDate: string | null,
): 'active' | 'due_soon' | 'overdue' | 'paused' | 'archived' | 'renewed' | 'lapsed' {
  if (status === 'lapsed') return 'lapsed';
  if (status === 'paused') return 'paused';
  if (status === 'archived' || status === 'renewed' || status === 'paid') {
    return status === 'archived' ? 'archived' : 'renewed';
  }
  if (!endDate) return 'active';
  const end = new Date(endDate + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diffDays = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 30) return 'due_soon';
  return 'active';
}

export function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

export function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
